import React from 'react';
import { IWarehouseMapping } from './inventory-breakdown.component';
import { ICartLineProps } from './cartlineitem.component';

interface ISupersessionBreakdownComponentProps {
    supersessionPreview: SupersessionPreview | undefined;
    props: ICartLineProps;
}

export interface SupersessionPreview {
    SourceItemId: string;
    ChainKey: string;
    RequestedQty: number;
    Allocations: Allocation[];
}

interface Allocation {
    ItemId: string;
    WarehouseId: string;
    Qty: number;
    IsBackorder: boolean;
    Name: string;
    Price: number;
}

export const SupersessionBreakdownComponent: React.FC<ISupersessionBreakdownComponentProps> = props => {
    const { supersessionPreview, props: cartLineProps } = props;
    const {
        substitutionLabel,
        priceLabel,
        substitutionItemHeaderLabel,
        substitutionBackorderItemHeaderLabel,
        substitutionQtyLabel,
        substitutionWarehouseLabel
    } = cartLineProps;
    const alternateWarehouseMappings: IWarehouseMapping[] | undefined = cartLineProps.context.app.config.alternateWarehouseMappings;
    const freePriceMask = cartLineProps.context.app.config.freePriceMask || '-----';

    const all = (supersessionPreview?.Allocations ?? []).filter(a => a && a.ItemId !== supersessionPreview?.SourceItemId);

    const allocations = all.filter(a => !a.IsBackorder);
    const boAllocations = all.filter(a => a.IsBackorder);

    if (!allocations.length && !boAllocations.length) {
        return null;
    }

    const warehouseMappings: IWarehouseMapping[] = alternateWarehouseMappings || [];

    const getWarehouseName = (alloc: Allocation) =>
        warehouseMappings.find(m => m.warehouseId === alloc.WarehouseId)?.warehouseName ?? alloc.Name ?? alloc.WarehouseId;

    const renderTable = (rows: Allocation[], headerLabel: string) => {
        if (!rows.length) {
            return null;
        }

        return (
            <div className='substitution-preview__tableWrap'>
                <table className='substitution-preview__table'>
                    <thead>
                        <tr>
                            <th className='col-item'>{headerLabel}</th>
                            <th className='col-qty'>{substitutionQtyLabel}</th>
                            <th className='col-wh'>{substitutionWarehouseLabel}</th>
                            <th className='col-price'>{priceLabel}</th>
                        </tr>
                    </thead>

                    <tbody>
                        {rows.map((alloc, idx) => {
                            const qty = Number.isFinite(alloc.Qty) ? alloc.Qty : 0;
                            const whName = getWarehouseName(alloc);

                            return (
                                <tr key={`${alloc.ItemId}-${alloc.WarehouseId}-${idx}`}>
                                    <td className='cell-item'>
                                        <div className='cell-item__content'>
                                            <span className='item-id'>{alloc.ItemId}</span>
                                        </div>
                                    </td>

                                    <td className='cell-qty'>{qty}</td>

                                    <td className='cell-wh'>
                                        <span className='warehouse-name'>{whName}</span>
                                    </td>

                                    <td className='cell-price'>
                                        <span className='price-placeholder'>
                                            {alloc.Price
                                                ? cartLineProps.context.cultureFormatter.formatCurrency(alloc.Price)
                                                : freePriceMask}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className='substitution-preview' role='note' aria-label={substitutionLabel}>
            <div className='substitution-preview__header'>
                <span className='substitution-preview__title'>{substitutionLabel}</span>
            </div>

            <div className='substitution-preview__tables'>
                {renderTable(allocations, substitutionItemHeaderLabel)}
                {renderTable(boAllocations, substitutionBackorderItemHeaderLabel)}
            </div>
        </div>
    );
};
